import { Component, OnInit, inject, DestroyRef, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RouterLinkActive, RouterLink, Router, ActivatedRoute } from '@angular/router';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { NgStyle, AsyncPipe } from '@angular/common';
import { MyCompanyProductRoles } from 'src/app/models/my_company_product_roles.model';
import { Department } from 'src/app/models/department.model';
import { PlatformService } from 'src/app/services/platform.service';
import { MatDialog } from '@angular/material/dialog';
import { MatRippleModule } from '@angular/material/core';
import { UpdateAvailableDialogComponent } from '../../shared/update-available-dialog/update-available-dialog.component';
import { MenuSectionEnum } from 'src/app/enums/menu-section.enum';
import { ROUTER_COMPONENT_RELOAD_URL } from 'src/app/redirect-to-role-home';
import { PendingActionRoute } from 'src/app/models/actions-required.interface';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { map } from 'rxjs';
import { ActiveCompanyObjective } from 'src/app/models/active-company-objective.model';
import { TruncatedTextMarqueeComponent } from './truncated-text-marquee.component';
import { CompanyUserMemberRoleEnum } from 'src/app/enums/company-user-member-role.enum';

@Component({
  selector: 'app-lateral-menu-employee',
  templateUrl: './lateral-menu-employee.component.html',
  styleUrls: ['./lateral-menu-employee.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLinkActive, RouterLink, MatIcon, NgStyle, AsyncPipe, MatIconModule, MatRippleModule, TruncatedTextMarqueeComponent],
})
export class LateralMenuEmployeeComponent implements OnInit {
  private static readonly sections_expanded_key = 'lateralMenuSectionsExpanded';

  private static readonly pending_action_routes: ReadonlyArray<PendingActionRoute> = [
    { key: 'pending_schedule_change_requests_count', route: ['/employees', 'rrhh', 'clock-register-review-requests'] },
    {
      key: 'pending_weekly_schedule_exception_requests_count',
      route: ['/employees', 'rrhh', 'working-contract-weekly-schedule-exception-requests'],
    },
    { key: 'pending_absence_requests_count', route: ['/employees', 'rrhh', 'absences-management'] },
    { key: 'pending_learning_resource_requests_count', route: ['/employees', 'rrhh', 'learning-resource-requests'] },
    { key: 'pending_extra_hours_requests_count', route: ['/employees', 'rrhh', 'working-extra-hours-periods'] },
    { key: 'pending_responsible_remote_work_requests_count', route: ['/employees', 'rrhh', 'remote-work-management'] },
    { key: 'pending_responsible_driving_requests_count', route: ['/employees', 'rrhh', 'driving-requests'] },
    { key: 'pending_meeting_attendances_count', route: ['/employees', 'activity', 'calendar'] },
    { key: 'pending_potential_clients_to_contact_count', route: ['/employees', 'comercial', 'potential-clients'] },
    { key: 'pending_invoice_conciliations_count', route: ['/employees', 'production', 'company-product-monthly-economic-reports'] },
    { key: 'pending_slack_chat_connections_count', route: ['/employees', 'my-profile', 'chats'] },
    {
      key: 'pending_unassigned_messaging_conversations_count',
      route: ['/employees', 'configuration', 'messaging-conversations'],
    },
    {
      key: 'pending_unassigned_workspaces_count',
      route: ['/employees', 'configuration', 'workspaces'],
    },
  ];

  private readonly destroy_ref = inject(DestroyRef);
  readonly permissions = inject(PermissionsService);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly employee = inject(EmployeeService);
  readonly platform = inject(PlatformService);
  private readonly dialog = inject(MatDialog);
  readonly MenuSectionEnum = MenuSectionEnum;

  readonly pendingAbsenceRequestsCount = computed((): number => {
    return this.employee.actionsRequired().pending_absence_requests_count;
  });

  readonly pendingScheduleChangeRequestsCount = computed((): number => {
    return this.employee.actionsRequired().pending_schedule_change_requests_count;
  });

  readonly pending_weekly_schedule_exception_requests_count = computed((): number => {
    return this.employee.actionsRequired().pending_weekly_schedule_exception_requests_count;
  });

  readonly pendingExtraHoursRequestsCount = computed((): number => {
    return this.employee.actionsRequired().pending_extra_hours_requests_count;
  });

  private readonly currentDepartments = toSignal(this.permissions.current_departments_observable, { initialValue: [] as Department[] });

  readonly canSeeRrhhSection = computed((): boolean => {
    const currentDepartments = this.currentDepartments();
    return currentDepartments.some(department => department.slug === 'direction' || department.slug === 'rrhh');
  });

  readonly canSeeRrhhMenu = computed((): boolean => {
    return this.canSeeRrhhSection() || this.canSeeRrhhExtraHoursAsResponsible() || this.canSeeRrhhRemoteWorkAsResponsible();
  });

  readonly canSeeRrhhExtraHoursAsResponsible = computed((): boolean => {
    return !this.canSeeRrhhSection() && this.employee.actionsRequired().is_extra_hours_responsible;
  });

  readonly canSeeRrhhExtraHoursMenuItem = computed((): boolean => {
    return this.canSeeRrhhSection() || this.canSeeRrhhExtraHoursAsResponsible();
  });

  readonly canSeeRrhhRemoteWorkAsResponsible = computed((): boolean => {
    return !this.canSeeRrhhSection() && this.employee.actionsRequired().is_remote_work_responsible;
  });

  readonly canSeeRrhhRemoteWorkMenuItem = computed((): boolean => {
    return this.canSeeRrhhSection() || this.canSeeRrhhRemoteWorkAsResponsible();
  });

  readonly canSeeRrhhDrivingAsResponsible = computed((): boolean => {
    return !this.canSeeRrhhSection() && this.employee.actionsRequired().is_driving_responsible;
  });

  readonly canSeeRrhhDrivingMenuItem = computed((): boolean => {
    return this.canSeeRrhhSection() || this.canSeeRrhhDrivingAsResponsible();
  });

  readonly pendingRemoteWorkRequestsCount = computed((): number => {
    return this.employee.actionsRequired().pending_responsible_remote_work_requests_count;
  });

  readonly pendingDrivingRequestsCount = computed((): number => {
    return this.employee.actionsRequired().pending_responsible_driving_requests_count;
  });

  readonly pendingLearningResourceRequestsCount = computed((): number => {
    return this.employee.actionsRequired().pending_learning_resource_requests_count;
  });

  readonly pendingPotentialClientsToContactCount = computed((): number => {
    return this.employee.actionsRequired().pending_potential_clients_to_contact_count;
  });

  readonly pending_invoice_conciliations_count = computed((): number => {
    return this.employee.actionsRequired().pending_invoice_conciliations_count;
  });

  readonly pending_unassigned_messaging_conversations_count = computed((): number => {
    return this.employee.actionsRequired().pending_unassigned_messaging_conversations_count ?? 0;
  });

  readonly pending_unassigned_workspaces_count = computed((): number => {
    return this.employee.actionsRequired().pending_unassigned_workspaces_count ?? 0;
  });

  readonly pending_configuration_actions_count = computed((): number => {
    return this.pending_unassigned_messaging_conversations_count() + this.pending_unassigned_workspaces_count();
  });

  readonly pending_production_actions_count = computed((): number => {
    return this.pending_invoice_conciliations_count();
  });

  readonly pending_rrhh_actions_count = computed((): number => {
    let count = 0;

    if (this.canSeeRrhhSection()) {
      count += this.pendingAbsenceRequestsCount();
      count += this.pendingScheduleChangeRequestsCount();
      count += this.pending_weekly_schedule_exception_requests_count();
      count += this.pendingLearningResourceRequestsCount();
      count += this.pendingDrivingRequestsCount();
    }

    if (this.canSeeRrhhExtraHoursMenuItem()) {
      count += this.pendingExtraHoursRequestsCount();
    }

    if (this.canSeeRrhhRemoteWorkMenuItem()) {
      count += this.pendingRemoteWorkRequestsCount();
    }

    return count;
  });

  company_product: CompanyProduct | undefined;

  private readonly my_company_product_roles = signal<MyCompanyProductRoles | null>(null);

  readonly company_products_list = computed((): MyCompanyProductRoles[number][] => {
    const my_company_product_roles = this.my_company_product_roles();

    return my_company_product_roles === null ? [] : Object.values(my_company_product_roles);
  });

  readonly has_multiple_company_products = computed((): boolean => this.company_products_list().length > 1);
  readonly pending_bisual_internal_slack_config_by_company_product_id = computed((): Record<number, true> => {
    const ids = this.employee.actionsRequired().pending_bisual_internal_team_slack_channel_config_company_product_ids;
    const map: Record<number, true> = {};
    for (const company_product_id of ids) {
      map[company_product_id] = true;
    }

    return map;
  });

  readonly pending_bot_not_member_by_company_product_id = computed((): Record<number, true> => {
    const ids = this.employee.actionsRequired().pending_bot_not_member_messaging_conversation_company_product_ids;
    const map: Record<number, true> = {};
    for (const company_product_id of ids) {
      map[company_product_id] = true;
    }

    return map;
  });

  readonly pending_unconfigured_applications_by_company_product_id = computed((): Record<number, true> => {
    const ids = this.employee.actionsRequired().pending_unconfigured_applications_company_product_ids ?? [];
    const map: Record<number, true> = {};
    for (const company_product_id of ids) {
      map[company_product_id] = true;
    }

    return map;
  });

  readonly active_objective_title_by_company_product_id = toSignal(
    this.api.getMyActiveCompanyObjectives(new Date()).pipe(
      map((objectives: ActiveCompanyObjective[]): Record<number, string> => {
        const title_by_product_id: Record<number, string> = {};
        for (const objective of objectives) {
          if (objective.company_product_id !== null) {
            title_by_product_id[objective.company_product_id] = objective.title;
          }
        }

        return title_by_product_id;
      })
    ),
    {
      initialValue: {} as Record<number, string>,
    }
  );

  readonly single_company_product = computed(() => {
    const company_products = this.company_products_list();
    return company_products.length === 1 ? company_products[0] : null;
  });

  readonly canSeeProjectsSection = computed((): boolean => {
    const department_slugs = this.currentDepartments().map(department => department.slug);
    if (department_slugs.includes('development') || department_slugs.includes('designers') || department_slugs.includes('direction')) {
      return true;
    }

    return this.company_products_list().length > 0;
  });

  readonly is_meeting_mode_enabled = signal<boolean>(false);

  private readonly sections_expanded = signal<Partial<Record<MenuSectionEnum, boolean>>>(this.readSectionsExpanded());

  ngOnInit(): void {
    this.listenMeetingModeCompanyProduct();
    this.initCompanyProducts();
  }

  goToCompanyProductPhases(company_product_id: number): void {
    const current_params = { ...this.route.snapshot.queryParams };
    const has_pending_internal_slack_config =
      this.pending_bisual_internal_slack_config_by_company_product_id()[company_product_id] === true;
    const has_pending_bot_not_member = this.pending_bot_not_member_by_company_product_id()[company_product_id] === true;
    const has_pending_unconfigured_applications =
      this.pending_unconfigured_applications_by_company_product_id()[company_product_id] === true;
    const target_tab = has_pending_internal_slack_config
      ? 'communication'
      : has_pending_bot_not_member
        ? 'communication'
        : has_pending_unconfigured_applications
          ? 'applications'
          : 'okr-and-metrics';

    this.router.navigateByUrl(ROUTER_COMPONENT_RELOAD_URL, { skipLocationChange: true }).then((): void => {
      this.router.navigate(['/employees', 'development', 'company-products', company_product_id, target_tab], {
        queryParams: current_params,
      });
    });
  }

  private static readonly section_url_segments: Record<MenuSectionEnum, string> = {
    [MenuSectionEnum.COMPANY_PRODUCTS]: '/employees/development/company-products',
    [MenuSectionEnum.ACTIVITY]: '/employees/activity/',
    [MenuSectionEnum.PERFORMANCE]: '/employees/performance/',
    [MenuSectionEnum.PERSONAL]: '/employees/personal/',
    [MenuSectionEnum.MARKETING]: '/employees/marketing/',
    [MenuSectionEnum.SUPPORT]: '/employees/support/',
    [MenuSectionEnum.RRHH]: '/employees/rrhh/',
    [MenuSectionEnum.COMERCIAL]: '/employees/comercial/',
    [MenuSectionEnum.PRODUCTION]: '/employees/production/',
    [MenuSectionEnum.DIRECTION]: '/employees/direction/',
    [MenuSectionEnum.CONFIGURATION]: '/employees/configuration/',
    [MenuSectionEnum.INFO]: '/employees/wiki',
  };

  isSectionExpanded(section: MenuSectionEnum): boolean {
    return this.sections_expanded()[section] ?? true;
  }

  isSectionActive(section: MenuSectionEnum): boolean {
    return this.router.url.includes(LateralMenuEmployeeComponent.section_url_segments[section]);
  }

  toggleSection(section: MenuSectionEnum): void {
    this.sections_expanded.update((sections: Partial<Record<MenuSectionEnum, boolean>>): Partial<Record<MenuSectionEnum, boolean>> => {
      const next_sections = { ...sections, [section]: !(sections[section] ?? true) };
      localStorage.setItem(LateralMenuEmployeeComponent.sections_expanded_key, JSON.stringify(next_sections));
      return next_sections;
    });
  }

  onAccordionKeydown(event: KeyboardEvent, section: MenuSectionEnum): void {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    this.toggleSection(section);
  }

  isCompanyProductActive(company_product_id: number): boolean {
    const match = this.router.url.match(/\/employees\/development\/company-products\/(\d+)/);
    return match?.[1] === String(company_product_id);
  }

  private readSectionsExpanded(): Partial<Record<MenuSectionEnum, boolean>> {
    const stored = localStorage.getItem(LateralMenuEmployeeComponent.sections_expanded_key);

    if (stored == null) {
      return {};
    }

    try {
      return JSON.parse(stored) as Partial<Record<MenuSectionEnum, boolean>>;
    } catch {
      return {};
    }
  }

  private listenMeetingModeCompanyProduct(): void {
    this.employee.meetingModeCompanyProduct
      .pipe(
        switchMap((company_product_id: number | null) =>
          company_product_id != null ? this.api.getCompanyProduct(company_product_id.toString()) : of(undefined)
        ),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe((company_product: CompanyProduct | undefined) => {
        this.company_product = company_product;
        this.is_meeting_mode_enabled.set(!!company_product);
      });
  }

  private initCompanyProducts(): void {
    this.permissions.myCompanyProductRolesObservable
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((data: MyCompanyProductRoles | null): void => {
        this.my_company_product_roles.set(data);
      });
  }

  openUpdateDialog(): void {
    const version = this.platform.newVersionAvailable();
    if (!version) {
      return;
    }

    this.dialog
      .open(UpdateAvailableDialogComponent, {
        width: '500px',
        data: { version },
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_restart: boolean): void => {
        if (should_restart) {
          this.platform.restartAndInstallUpdate();
        }
      });
  }

  isExamsSectionActive(): boolean {
    const url = this.router.url;

    return url.includes('/exam-instances') || url.includes('/exam-templates') || url.includes('/skill-questions');
  }

  openFirstPendingAction(event: Event): void {
    event.preventDefault();

    const actions = this.employee.actionsRequired();

    for (const item of LateralMenuEmployeeComponent.pending_action_routes) {
      if (actions[item.key] <= 0) {
        continue;
      }

      if (item.key === 'pending_invoice_conciliations_count') {
        void this.router.navigate(this.resolvePendingInvoiceConciliationsRoute());
        return;
      }

      void this.router.navigate(item.route);
      return;
    }
  }

  private resolvePendingInvoiceConciliationsRoute(): string[] {
    const company_roles = this.permissions.getMyRoles();
    const first_account_manager_company_product_id = Object.values(company_roles ?? {})
      .filter(company => company.roles.some(role => role.slug === CompanyUserMemberRoleEnum.ACCOUNT_MANAGER))
      .flatMap(company => company.company_product_ids ?? [])
      .sort((left, right) => left - right)[0];

    if (first_account_manager_company_product_id != null) {
      return [
        '/employees',
        'development',
        'company-products',
        String(first_account_manager_company_product_id),
        'company-product-monthly-economic-reports',
      ];
    }

    return ['/employees', 'production', 'company-product-monthly-economic-reports'];
  }
}
